// ---------------------------------------------------------------------------
// Agent — workspace-scoped, versioned definition of "the thing under test".
// Mirrors the Agent section of the Workflow Service api-spec.
//
// Two concrete agent types ship today (`AgentLlmSpec` and
// `AgentHttpProxySpec`). The webapp only authors LLM agents in v1 of the
// eval-template create flow; the HTTP-proxy form is not yet drawn.
// ---------------------------------------------------------------------------

import type { SystemProperties } from './common.js';

export const AGENT_NAME_PATTERN = /^[a-z][a-z0-9-]*$/;
export const AGENT_NAME_MAX_LENGTH = 63;
export const AGENT_DISPLAY_NAME_MAX_LENGTH = 100;
export const AGENT_DESCRIPTION_MAX_LENGTH = 500;

// Data types supported for user-defined variables (mirrors the server-side
// VariableType enum).
export type VariableType = 'String' | 'Number' | 'Integer' | 'Boolean';

export const VARIABLE_TYPES: readonly VariableType[] = [
  'String',
  'Number',
  'Integer',
  'Boolean',
];

/**
 * A user-defined variable available for substitution in the agent
 * configuration. `default` is loosely typed because its runtime shape depends
 * on `type` (string / number / boolean).
 */
export interface VariableDeclaration {
  type: VariableType;
  default?: unknown;
  description?: string;
}

/**
 * LLM-init parameters.
 *
 * Note: numeric-looking fields are typed as `string` because the server
 * accepts variable references (e.g. `"$TEMPERATURE"`) interchangeably with
 * literal numbers. Pass numbers as strings (`"0.3"`).
 */
export interface AgentLlmInit {
  modelName: string;
  systemPrompt: string;
  temperature?: string;
  topP?: string;
  maxTokens?: string;
  contextWindow?: string;
  includeConversationHistory?: string;
}

export interface AgentLlmInvoke {
  /**
   * User-message template applied per trial. May contain server-side
   * `$expr:` template expressions that pull from the trial's query/data
   * item. The eval-template UI exposes this as a free-text field — the
   * exact templating syntax is owned by the framework and is not
   * validated client-side.
   */
  userMessage: string;
}

export interface AgentLlmSpec {
  agentType: 'AgentLlmSpec';
  init: AgentLlmInit;
  invoke: AgentLlmInvoke;
}

export const AGENT_LLM_SPEC_TYPE = 'AgentLlmSpec';

export interface AgentHttpProxySpec {
  agentType: 'AgentHttpProxySpec';
  // Field shape not yet drawn in the eval-template UI mockup; the v1 form
  // disables this option with a note. Server-side fields exist on
  // WorkflowSvc.Api.Contracts.AgentHttpProxySpec when we need them.
}

// Creation-time configuration for a grader-as-agent adapter: the grader whose
// scoring is promoted into the subject-under-test slot.
export interface GraderAsAgentInit {
  graderName: string;
}

/**
 * Adapts a grader so its scoring runs as the agent under test (e.g. grading a
 * grader). References a grader by name; the grader's own invoke configuration
 * is resolved from the referenced grader spec, so `invoke` carries no fields.
 */
export interface GraderAsAgentSpec {
  agentType: 'GraderAsAgentSpec';
  init: GraderAsAgentInit;
  invoke?: Record<string, never>;
}

export const GRADER_AS_AGENT_SPEC_TYPE = 'GraderAsAgentSpec';

export type AgentSpec = AgentLlmSpec | AgentHttpProxySpec | GraderAsAgentSpec;

export interface Agent {
  name: string;
  displayName?: string;
  description?: string;
  spec?: AgentSpec;
  variables?: Record<string, VariableDeclaration>;
  metadata?: Record<string, string>;
  isDraft: boolean;
  systemProperties: SystemProperties;
}

export interface AgentVersion extends Agent {
  version: number;
}

export interface UpsertAgentBody {
  displayName?: string;
  description?: string;
  spec?: AgentSpec;
  variables?: Record<string, VariableDeclaration>;
  metadata?: Record<string, string>;
}

// PATCH leaves omitted (undefined) fields unchanged; a provided map replaces
// the stored map wholesale.
export interface PatchAgentBody {
  displayName?: string;
  description?: string;
  spec?: AgentSpec;
  variables?: Record<string, VariableDeclaration>;
  metadata?: Record<string, string>;
}

export function isValidAgentName(name: string): boolean {
  return (
    name.length > 0 &&
    name.length <= AGENT_NAME_MAX_LENGTH &&
    AGENT_NAME_PATTERN.test(name)
  );
}

export function isLlmSpec(spec: AgentSpec | undefined): spec is AgentLlmSpec {
  return spec?.agentType === AGENT_LLM_SPEC_TYPE;
}
